/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Comandero F4 — Loyalty and promotions (PURE LOGIC)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CC-6: this file is PURE `.ts`. Does NOT import lingui macros or any UI.
 * Takes numbers (cents / points / basis points) and returns numbers.
 *
 * Convenciones:
 *   - Montos en enteros de unidades menores (centavos).
 *   - Loyalty points: integers.
 *   - Promo `percent`: `value` en basis points (1000 = 10%).
 *   - Promo `amount`: `value` en centavos.
 *
 * IMPORTANT: points accrual rule ALSO lives in Rust write-path
 * (`accrue_loyalty_for_order`) which is balance source of truth. Here
 * SAME rate is replicated to preview in UI before checkout.
 */

/** Total cents worth 1 point (must match LOYALTY_CENTS_PER_POINT in Rust). */
export const LOYALTY_CENTS_PER_POINT = 100;

/** Discount value in cents of 1 point when redeemed. */
export const LOYALTY_POINT_VALUE_CENTS = 1;

/**
 * Points accrued by an order for its net total (cents).
 * 1 point per `LOYALTY_CENTS_PER_POINT` cents; truncates remainder.
 */
export function pointsEarnedForTotal(netTotalCents: number): number {
  const t = Math.max(0, Math.trunc(netTotalCents));
  return Math.floor(t / LOYALTY_CENTS_PER_POINT);
}

/**
 * Discount in cents from redeeming `points`, capped at:
 *   - customer's available points (`available`), and
 *   - total to charge (`maxCents`) — cannot discount more than check.
 * Returns consistent `{ pointsToRedeem, discountCents }`.
 */
export function redeemPoints(
  pointsRequested: number,
  available: number,
  maxCents: number,
): { pointsToRedeem: number; discountCents: number } {
  const req = Math.max(0, Math.trunc(pointsRequested));
  const have = Math.max(0, Math.trunc(available));
  const cap = Math.max(0, Math.trunc(maxCents));
  // Max points redeemable without exceeding total.
  const maxByCents = Math.floor(cap / LOYALTY_POINT_VALUE_CENTS);
  const pointsToRedeem = Math.min(req, have, maxByCents);
  return { pointsToRedeem, discountCents: pointsToRedeem * LOYALTY_POINT_VALUE_CENTS };
}

/** Point balance = sum of movements (accumulations + signed redemptions). */
export function loyaltyBalance(deltas: number[]): number {
  return deltas.reduce((acc, d) => acc + Math.trunc(d), 0);
}

export type PromotionKind = "percent" | "amount";

export interface PromotionRule {
  kind: PromotionKind;
  /** percent: basis points (1000 = 10%); amount: centavos. */
  value: number;
  /** Happy hour: hora de inicio 0-23 (null/undefined = siempre activa). */
  startHour?: number | null;
  /** Happy hour: hora de fin 0-23 (null/undefined = siempre activa). */
  endHour?: number | null;
  isActive?: boolean;
}

const BASIS_POINTS = 10_000;

/**
 * Does promo apply at `hour` (0-23)?
 * No time window → always. Window is [start, end): supports crossing
 * medianoche (p.ej. 22→2 cubre 22,23,0,1).
 */
export function isPromotionActiveAt(promo: PromotionRule, hour: number): boolean {
  if (promo.isActive === false) {
    return false;
  }
  const h = Math.trunc(hour);
  const start = promo.startHour;
  const end = promo.endHour;
  if (start == null || end == null) {
    return true;
  }
  if (start === end) {
    return true; // ventana de 24h
  }
  if (start < end) {
    return h >= start && h < end;
  }
  // Cruza medianoche.
  return h >= start || h < end;
}

/**
 * Discount in cents of promo over `subtotalCents`.
 * `percent` rounds to nearest cent; `amount` is capped at subtotal. 0 if not applicable.
 */
export function promotionDiscount(
  promo: PromotionRule,
  subtotalCents: number,
  hour: number,
): number {
  if (!isPromotionActiveAt(promo, hour)) {
    return 0;
  }
  const subtotal = Math.max(0, Math.trunc(subtotalCents));
  if (promo.kind === "percent") {
    const raw = Math.round((subtotal * Math.max(0, promo.value)) / BASIS_POINTS);
    return Math.min(raw, subtotal);
  }
  return Math.min(Math.max(0, Math.trunc(promo.value)), subtotal);
}
